import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, FileText, Upload, Trash2, Tag, Search, Copy, CheckCircle, ExternalLink, Download, ShieldCheck, AlertTriangle, FileWarning, Loader2, AlertCircle, X } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';

const TYPE_COLORS = { prompt: '#FF4D00', research: '#3B82F6', document: '#22C55E', spec: '#A855F7' };

// #725: binary_status badges distinguish whether the original file is retrievable.
// #759 adds 'not_retained' for the markdown-only path on large uploads.
function BinaryStatusBadge({ status }) {
  if (!status || status === 'not_applicable') return null;
  const config = {
    stored:       { label: 'Original available',  icon: ShieldCheck,   color: '#22C55E' },
    pending:      { label: 'Upload pending',      icon: Upload,        color: '#3B82F6' },
    failed:       { label: 'Original lost',       icon: AlertTriangle, color: '#EF4444' },
    not_found:    { label: 'Markdown only',       icon: FileWarning,   color: '#F2A900' },
    not_retained: { label: 'Markdown only · large', icon: FileWarning, color: '#F2A900' },
  }[status];
  if (!config) return null;
  const Icon = config.icon;
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 border uppercase tracking-wider"
      style={{ color: config.color, borderColor: config.color + '40' }}
      title={
        status === 'failed' ? 'Storage upload failed — only the markdown rendering is available' :
        status === 'not_found' ? 'Original file is no longer in Storage — showing markdown fallback' :
        status === 'not_retained' ? 'File exceeded the 10 MB retention limit — original was not kept, only the markdown rendering is available' :
        status === 'pending' ? 'Storage upload in progress' :
        'Original file is preserved in encrypted storage'
      }
    >
      <Icon className="w-2.5 h-2.5" />
      {config.label}
    </span>
  );
}

export default function ContextRepo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contexts, setContexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newType, setNewType] = useState('prompt');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedContent, setExpandedContent] = useState('');

  // #749: Phase-aware upload state. Drives the progress bar UI.
  // null when idle. Otherwise an object with:
  //   phase: 'wire' | 'server' | 'error'
  //   filename, percent (0-100), fileSize (bytes)
  //   serverPhase, serverLabel    (server phase only)
  //   message, file               (error phase only)
  //   startedAt                   (Date.now() at POST submit, for elapsed timer)
  //   lastPctChangeAt             (timestamp of last percent change, for heartbeat pulse)
  //   throughputBps               (wire phase only — bytes/sec from onUploadProgress)
  //   throughputSlow              (true if wire throughput dropped below threshold)
  //   phaseStartedAt              (server phase only — for #753 sub-phase interpolation)
  //   staleHeartbeat              (true if backend heartbeat is >30s stale)
  const [uploadState, setUploadState] = useState(null);
  // #753: pre-upload size hint state. When user picks a large file we
  // hold it here briefly so they can confirm before kicking off the POST.
  const [pendingUpload, setPendingUpload] = useState(null);
  // Retain the last-attempted file so the Retry button can re-fire
  // performUpload without forcing the user to re-pick.
  const lastFileRef = useRef(null);
  // #753: re-render driver — both the elapsed timer and sub-phase
  // interpolation need a wall-clock tick. One state-bumping interval
  // covers both rather than spinning two timers.
  const [tick, setTick] = useState(0);

  useEffect(() => { loadContexts(); }, []);

  const loadContexts = async () => {
    try {
      const res = await API.get('/api/contexts');
      setContexts(res.data.contexts || []);
    } catch {}
    setLoading(false);
  };

  const handleSave = async () => {
    if (!newContent.trim()) return;
    setSaving(true);
    try {
      await API.post('/api/contexts', { title: newTitle, content: newContent, type: newType });
      setNewTitle(''); setNewContent(''); setShowCreate(false);
      await loadContexts();
    } catch {}
    setSaving(false);
  };

  // #749: Two-phase upload flow with real progress bar.
  //   Phase 1 (wire): axios.onUploadProgress drives 0-30%.
  //   Phase 2 (server): poll /upload-status/{job_id} every 500ms,
  //                     map server phase to 30-100%.
  // Server phase → percent mapping (matches backend _PHASE_PERCENT).
  const SERVER_PHASE_PCT = {
    received:          30,
    converting:        45,
    encrypting:        60,
    uploading_storage: 80,
    inserting:         95,
    done:              100,
  };
  const SERVER_PHASE_LABEL = {
    received:          'Queued for processing',
    converting:        'Converting to markdown',
    encrypting:        'Encrypting (AES-256-GCM)',
    uploading_storage: 'Uploading to Storage',
    inserting:         'Saving',
    done:              'Done',
  };

  // #753: thresholds for over-communication. Tuned conservatively —
  // anything below 2MB feels "fast" enough that an extra warning is noise.
  const SIZE_HINT_THRESHOLD = 2 * 1024 * 1024;        // 2 MB — pre-upload hint
  const XL_THRESHOLD        = 5 * 1024 * 1024;        // 5 MB — copy + tab-close warning
  const HEARTBEAT_STALE_MS  = 30_000;                 // 30s — backend heartbeat gap
  const PCT_PULSE_MS        = 2_000;                  // 2s — bar pulse if no movement
  const SLOW_THROUGHPUT_BPS = 100 * 1024;             // 100 KB/s — slow connection
  // #757/#759: hard per-file cap enforced by the backend in save_context_from_file.
  // Check client-side too so we don't waste a multi-MB upload across the wire
  // just to get a 400 back. Must match backend constant.
  const MAX_UPLOAD_BYTES    = 25 * 1024 * 1024;       // 25 MB — hard limit
  // #759: files above this retain markdown only, backend drops the binary.
  // Must match backend BINARY_RETENTION_LIMIT in _process_upload_job.
  const BINARY_RETENTION_LIMIT = 10 * 1024 * 1024;    // 10 MB — retention cutoff
  // #761/#763: wire phase has two failure modes with different reasonable
  // timeouts. Collapsing them into one (45s) fired false alarms on 14 MB
  // PDFs where the 202 response was legitimately delayed behind markitdown
  // starting or edge proxy buffering.
  //   send:  bytes actively flowing via onUploadProgress. If no progress
  //          event for SEND ms, the wire is genuinely stuck (connection
  //          dropped, proxy killed us). Short timeout is appropriate.
  //   await: bytes fully sent (e.loaded === e.total) but the POST promise
  //          hasn't resolved — we're waiting for the server's 202.
  //          Can legitimately take minutes on big files.
  const WIRE_SEND_TIMEOUT_MS   = 45_000;              // 45s — bytes not flowing
  const WIRE_ACCEPT_TIMEOUT_MS = 300_000;             // 5min — waiting for 202 ack
  // Sub-phase interpolation ranges. Each phase claims [start, end] of the
  // overall bar; elapsed time within phase drives a linear creep from
  // start toward end (capped — never crosses the next phase's start).
  const SERVER_PHASE_RANGES = {
    received:          [25, 30],
    converting:        [30, 55],
    encrypting:        [55, 60],
    uploading_storage: [60, 90],
    inserting:         [90, 98],
  };
  // Per-phase duration estimates (ms). Conversion and storage scale with
  // file size — small fixed for crypto/insert. Used only for the frontend
  // illusion; actual phase boundaries still come from backend updates.
  const estimatePhaseDurationMs = (phase, fileSize) => {
    const mb = Math.max(1, (fileSize || 0) / (1024 * 1024));
    switch (phase) {
      case 'converting':        return Math.min(90_000, 3_000 * mb);
      case 'uploading_storage': return Math.min(60_000, 2_000 * mb);
      case 'encrypting':        return 1_500;
      case 'inserting':         return 1_500;
      default:                  return 1_000;
    }
  };
  const formatBytes = (b) => {
    if (b == null) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };
  const formatElapsed = (ms) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    return `${m}:${(s % 60).toString().padStart(2, '0')}`;
  };
  const formatThroughput = (bps) => {
    if (!bps || bps < 1024) return '';
    if (bps < 1024 * 1024) return `${(bps / 1024).toFixed(0)} KB/s`;
    return `${(bps / (1024 * 1024)).toFixed(1)} MB/s`;
  };

  // #753: drive a re-render every 500ms while a phase is active so the
  // elapsed timer ticks and the sub-phase interpolation creeps. Cheap —
  // setTick just bumps a counter, real values come from uploadState +
  // Date.now() reads inline.
  useEffect(() => {
    if (!uploadState || uploadState.phase === 'error') return;
    const id = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, [uploadState?.phase]);

  // #753: register beforeunload while an upload is active for files
  // above XL threshold — accidental tab close on a 12MB upload loses
  // the work. Suppressed for small files (the warning would be noise).
  useEffect(() => {
    if (!uploadState || (uploadState.fileSize || 0) < XL_THRESHOLD) return;
    if (uploadState.phase !== 'wire' && uploadState.phase !== 'server') return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';  // chrome requires this assignment
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploadState?.phase, uploadState?.fileSize]);

  // #761/#763: wire-phase watchdog with split timeouts.
  //   Before bytes complete: watch lastProgressAt — if 45s elapses
  //     without any onUploadProgress event, connection is stuck.
  //   After bytes complete: watch bytesSentAt — give the server 5 min
  //     to return its 202. Emergent's edge has shown it can legitimately
  //     take over a minute on 14 MB+ files.
  useEffect(() => {
    if (!uploadState || uploadState.phase !== 'wire') return;

    const bailToError = (message) => {
      setUploadState((s) => s && s.phase === 'wire' ? {
        phase: 'error',
        filename: s.filename,
        fileSize: s.fileSize,
        file: s.file,
        startedAt: s.startedAt,
        message,
      } : s);
    };

    let timerId;
    if (!uploadState.wireBytesSent) {
      // Still sending — watchdog resets on each progress event via
      // lastProgressAt; use that as the anchor rather than startedAt.
      const anchor = uploadState.lastProgressAt || uploadState.startedAt || Date.now();
      const remaining = WIRE_SEND_TIMEOUT_MS - (Date.now() - anchor);
      if (remaining <= 0) {
        bailToError('Upload stalled — no progress in 45s. Check your connection and retry.');
        return;
      }
      timerId = setTimeout(
        () => bailToError('Upload stalled — no progress in 45s. Check your connection and retry.'),
        remaining,
      );
    } else {
      // Bytes fully sent — waiting for 202 acknowledgement. Much longer
      // allowance because the backend may be starting markitdown before
      // it responds, or the edge proxy may be buffering the response.
      const anchor = uploadState.bytesSentAt || Date.now();
      const remaining = WIRE_ACCEPT_TIMEOUT_MS - (Date.now() - anchor);
      if (remaining <= 0) {
        bailToError('Server took too long to confirm. Your upload may still have succeeded — refresh to check the file list.');
        return;
      }
      timerId = setTimeout(
        () => bailToError('Server took too long to confirm. Your upload may still have succeeded — refresh to check the file list.'),
        remaining,
      );
    }
    return () => clearTimeout(timerId);
  }, [
    uploadState?.phase,
    uploadState?.wireBytesSent,
    uploadState?.bytesSentAt,
    uploadState?.lastProgressAt,
  ]);

  const performUpload = async (file) => {
    if (!file) return;
    lastFileRef.current = file;
    const startedAt = Date.now();
    // #769: ad-hoc instrumentation. Toggle via ?debugUploads=1 on the
    // URL — keeps the console quiet for normal users but lets us pull
    // a full timeline from the user's browser when diagnosing hangs.
    const debugUploads = new URLSearchParams(window.location.search)
      .get('debugUploads') === '1';
    const requestId = Math.random().toString(36).slice(2, 10);
    const debugLog = (...args) => {
      if (debugUploads) console.log(`[upload ${requestId}]`, ...args);
    };
    debugLog('POST start', file.name, `${(file.size / 1024 / 1024).toFixed(2)} MB`);

    setUploadState({
      phase: 'wire', filename: file.name, fileSize: file.size,
      percent: 0, startedAt,
      lastPctChangeAt: startedAt,
      lastProgressAt: startedAt,   // #763: for wire-send timeout
      wireBytesSent: false,        // #763: flips when e.loaded === e.total
      bytesSentAt: null,           // #763: anchor for wire-accept timeout
      throughputBps: 0, throughputSlow: false,
      requestId,                   // #769: surface for test-id correlation
    });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('context_type', 'document');

    // Throughput sampling — track previous tick's bytes/timestamp to
    // compute deltas. Decays to "slow" if rate stays below threshold.
    let lastSample = { loaded: 0, ts: startedAt };
    let slowSince = null;

    let jobId;
    try {
      const res = await API.post('/api/contexts/from-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          // #769: backend mirrors this back into its log lines so we
          // can correlate browser console with server logs across the
          // 5-min POST hang we're trying to diagnose.
          'X-Client-Request-Id': requestId,
        },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const wirePct = Math.min(30, Math.round((e.loaded / e.total) * 30));
          // Throughput: bytes per second based on delta from last sample
          const now = Date.now();
          const dtSec = Math.max(0.001, (now - lastSample.ts) / 1000);
          const bps = (e.loaded - lastSample.loaded) / dtSec;
          lastSample = { loaded: e.loaded, ts: now };
          // Slow-connection detection: low throughput sustained >3s
          if (bps > 0 && bps < SLOW_THROUGHPUT_BPS) {
            if (slowSince == null) slowSince = now;
          } else {
            slowSince = null;
          }
          const slow = slowSince != null && (now - slowSince) > 3000;
          // #763: mark wireBytesSent once all bytes acknowledged by browser
          const bytesSent = e.loaded >= e.total;
          // #769: log every progress event with elapsed seconds
          debugLog('progress',
            `${e.loaded}/${e.total}`,
            `${((now - startedAt) / 1000).toFixed(2)}s`,
            `${(bps / 1024 / 1024).toFixed(2)} MB/s`,
            bytesSent ? '(complete)' : '');
          setUploadState((s) => {
            if (!s || s.phase !== 'wire') return s;
            const pctChanged = wirePct !== s.percent;
            return {
              ...s,
              percent: wirePct,
              lastPctChangeAt: pctChanged ? now : s.lastPctChangeAt,
              lastProgressAt: now,  // #763: any progress event resets send-timer
              wireBytesSent: bytesSent || s.wireBytesSent,
              bytesSentAt: (bytesSent && !s.wireBytesSent) ? now : s.bytesSentAt,
              throughputBps: bps,
              throughputSlow: slow,
            };
          });
        },
      });
      // #769: this fires when the 202 actually reached the browser. The
      // gap between the last 'progress' line and this one is the time
      // we spent waiting for the server's response — the focal interval
      // when diagnosing edge / proxy buffering.
      debugLog('POST resolved', `status=${res.status}`,
        `total=${((Date.now() - startedAt) / 1000).toFixed(2)}s`,
        `job_id=${res.data?.job_id}`);
      jobId = res.data?.job_id;
      if (!jobId) throw new Error('Backend did not return a job_id');
    } catch (err) {
      // #769: capture the exact failure point + status code if any
      debugLog('POST failed',
        `status=${err?.response?.status || 'no-response'}`,
        `total=${((Date.now() - startedAt) / 1000).toFixed(2)}s`,
        err?.message || 'unknown error');
      const detail = err?.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : (err?.message || 'Upload failed.');
      setUploadState({
        phase: 'error', filename: file.name, fileSize: file.size,
        message, file, startedAt,
      });
      return;
    }

    // Phase 2: poll job status. Bounded by maxPolls so a stuck job
    // doesn't poll forever — at 500ms each, 600 polls = 5 minutes.
    const maxPolls = 600;
    let polls = 0;
    let lastBackendPhase = null;
    let phaseStartedAt = Date.now();
    while (polls < maxPolls) {
      polls += 1;
      await new Promise((r) => setTimeout(r, 500));
      let data;
      try {
        const status = await API.get(`/api/contexts/upload-status/${jobId}`);
        data = status.data;
      } catch (err) {
        if (polls < maxPolls - 5) continue;
        setUploadState({
          phase: 'error', filename: file.name, fileSize: file.size,
          file, startedAt,
          message: 'Lost connection to upload status — please refresh to check.',
        });
        return;
      }

      if (data.phase === 'done') {
        setUploadState(null);
        await loadContexts();
        return;
      }
      if (data.phase === 'failed') {
        setUploadState({
          phase: 'error', filename: file.name, fileSize: file.size,
          file, startedAt,
          message: data.error || 'Upload failed on the server.',
        });
        return;
      }
      // Phase boundary: reset phaseStartedAt so interpolation restarts
      if (data.phase !== lastBackendPhase) {
        lastBackendPhase = data.phase;
        phaseStartedAt = Date.now();
      }
      const basePct = SERVER_PHASE_PCT[data.phase] ?? 30;
      setUploadState((prev) => ({
        phase: 'server',
        filename: file.name,
        fileSize: file.size,
        startedAt,
        serverPhase: data.phase,
        serverLabel: SERVER_PHASE_LABEL[data.phase] || data.phase,
        percent: basePct,
        lastPctChangeAt: prev && prev.percent === basePct
          ? prev.lastPctChangeAt : Date.now(),
        phaseStartedAt,
        staleHeartbeat: (data.seconds_since_heartbeat || 0) * 1000 > HEARTBEAT_STALE_MS,
        secondsSinceHeartbeat: data.seconds_since_heartbeat || 0,
      }));
    }

    setUploadState({
      phase: 'error', filename: file.name, fileSize: file.size,
      file, startedAt,
      message: 'Upload taking longer than expected — refresh to check status.',
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';  // allow re-selecting the same file later
    if (!file) return;
    // #757: client-side size guard. Backend also enforces this — checking
    // here saves a multi-MB wasted POST and gives a clearer error shape
    // (size vs limit) than the generic "Upload failed" banner.
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadState({
        phase: 'error',
        filename: file.name,
        fileSize: file.size,
        file,
        noRetry: true,  // same file will fail again — retry can't help
        message: `${formatBytes(file.size)} exceeds the ${formatBytes(MAX_UPLOAD_BYTES)} per-file limit. Choose a smaller file.`,
      });
      return;
    }
    // #753: large files get a confirmation step so the user knows what
    // they're committing to before the bar starts. Smaller files go
    // straight through — the hint would just slow them down.
    if (file.size >= SIZE_HINT_THRESHOLD) {
      setPendingUpload(file);
      return;
    }
    await performUpload(file);
  };

  const confirmPendingUpload = async () => {
    const f = pendingUpload;
    setPendingUpload(null);
    if (f) await performUpload(f);
  };

  const cancelPendingUpload = () => setPendingUpload(null);

  const isUploadActive = (s) =>
    s && (s.phase === 'wire' || s.phase === 'server');

  const dismissUploadBanner = () => setUploadState(null);
  const retryUpload = () => {
    if (lastFileRef.current) performUpload(lastFileRef.current);
  };

  // #753: compute the displayed bar percentage. Server phases get a
  // sub-phase interpolation lift on top of the backend's coarse value so
  // the bar visibly creeps during long phases (markitdown chewing on a
  // big PDF). Wire phase uses the raw onUploadProgress value.
  // NOTE: this is a frontend illusion — the underlying job state is still
  // whatever the backend last reported.
  const displayPercent = (s) => {
    if (!s) return 0;
    if (s.phase !== 'server') return s.percent ?? 0;
    const range = SERVER_PHASE_RANGES[s.serverPhase];
    if (!range) return s.percent ?? 0;
    const [start, end] = range;
    const dur = estimatePhaseDurationMs(s.serverPhase, s.fileSize);
    const elapsed = Date.now() - (s.phaseStartedAt || Date.now());
    const frac = Math.min(1, elapsed / dur);
    // Cap at end - 1 so we never bump up against the next phase prematurely.
    return Math.min(end - 1, Math.round(start + frac * (end - start)));
  };

  // #753: size-aware phase copy. For >5MB files we extend the label
  // with a "this can take a while" tail so the user knows static bar
  // != stuck.
  const labelForServerPhase = (s) => {
    const base = s.serverLabel || s.serverPhase || '';
    if (!s.fileSize || s.fileSize < XL_THRESHOLD) return base;
    if (s.serverPhase === 'converting') {
      return `${base} (large file — this can take a minute)`;
    }
    if (s.serverPhase === 'uploading_storage') {
      return `${base} (large file — please wait)`;
    }
    return base;
  };

  // #753: determine if the bar should pulse. True when the displayed
  // percent hasn't changed in PCT_PULSE_MS — signals "alive" even when
  // the bar is stationary.
  const shouldPulseBar = (s) => {
    if (!s || s.phase === 'error') return false;
    return Date.now() - (s.lastPctChangeAt || Date.now()) > PCT_PULSE_MS;
  };

  // Reference `tick` so React lints don't complain it's unused.
  void tick;

  // #725/#767: download — split into markdown and original paths. The
  // backend accepts format=markdown|original query params; we pass one
  // explicitly so the downloaded extension can't ever be wrong (the old
  // single-icon path could fall back to original_filename which left
  // markdown content with a .pdf extension).
  const handleDownload = async (ctx, format) => {
    try {
      const res = await API.get(
        `/api/contexts/${ctx.id}/download?format=${format}`,
        { responseType: 'blob' },
      );
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/);
      // Trust the server's filename; only fall back to a computed name
      // if the header is genuinely missing. For markdown, always end in .md.
      let filename = match ? match[1] : null;
      if (!filename) {
        if (format === 'markdown') {
          const base = (ctx.original_filename || ctx.filename || ctx.id).replace(/\.[^.]+$/, '');
          filename = `${base}.md`;
        } else {
          filename = ctx.original_filename || ctx.filename || `${ctx.id}.bin`;
        }
      }
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed — see console for details.');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/api/contexts/${id}`);
      setContexts(prev => prev.filter(c => c.id !== id));
      if (expandedId === id) { setExpandedId(null); setExpandedContent(''); }
    } catch {}
  };

  const handleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    try {
      const res = await API.get(`/api/contexts/${id}`);
      setExpandedId(id);
      setExpandedContent(res.data.content || '');
    } catch {}
  };

  const handleCopyToImport = (content) => {
    // Copy to clipboard and navigate to import
    navigator.clipboard.writeText(content);
    setCopied('import');
    setTimeout(() => { navigate('/fray/import'); }, 500);
  };

  const filtered = contexts.filter(c =>
    !search || c.title?.toLowerCase().includes(search.toLowerCase()) || c.preview?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed']" data-testid="context-title">Context Repository</h1>
            <p className="text-[#A0A0A0] text-sm mt-1">
              Save prompts, research, and documents for reuse across Frays
              {' · '}
              <span className="text-[#A0A0A0]/80" data-testid="upload-size-limit-hint">
                Max {formatBytes(MAX_UPLOAD_BYTES)} per file
                {' · '}
                originals over {formatBytes(BINARY_RETENTION_LIMIT)} not retained
              </span>
            </p>
          </div>
          <div className="flex gap-2">
            {/* #749: upload button shows phase-aware label + spinner during upload */}
            <label
              className={`px-4 py-2 border text-sm flex items-center gap-2 ${
                isUploadActive(uploadState)
                  ? 'border-[#FF4D00] text-[#FF4D00] cursor-wait opacity-80'
                  : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00]/40 cursor-pointer'
              }`}
              data-testid="upload-context-btn"
              title={`Upload a document (max ${formatBytes(MAX_UPLOAD_BYTES)})`}
            >
              {isUploadActive(uploadState)
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Upload className="w-4 h-4" />}
              {isUploadActive(uploadState)
                ? `Uploading… ${displayPercent(uploadState)}%`
                : 'Upload File'}
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.md,.txt,.markdown,.csv,.json,.png,.jpg,.jpeg,.webp"
                onChange={handleFileUpload}
                disabled={isUploadActive(uploadState)}
                className="hidden"
              />
            </label>
            <button onClick={() => setShowCreate(!showCreate)}
              className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium flex items-center gap-2" data-testid="new-context-btn">
              <Plus className="w-4 h-4" /> New Context
            </button>
          </div>
        </div>

        {/* #753: Pre-upload size hint (Tier 1). Shown when user picks a
            file > 2 MB so they have a frame of reference before the bar
            starts and the post-button click commits them. */}
        <AnimatePresence>
          {pendingUpload && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-4"
              data-testid="upload-size-hint"
            >
              <div className="flex items-start gap-3 p-3 bg-[#F2A900]/10 border border-[#F2A900]/40">
                <AlertCircle className="w-4 h-4 text-[#F2A900] shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-[#F5F1E8] font-mono text-xs">
                    <span className="text-[#F2A900]">{pendingUpload.name}</span>
                    {' is '}<span className="tabular-nums">{formatBytes(pendingUpload.size)}</span>
                    {pendingUpload.size > BINARY_RETENTION_LIMIT ? (
                      // #759: 10–25 MB band. Original is NOT retained — be explicit.
                      <> — over the {formatBytes(BINARY_RETENTION_LIMIT)} retention
                        limit, so the <strong>original will not be kept</strong>. We'll
                        extract the content for Frays (markdown only). Converting may
                        take ~1 minute. Stay on this page.</>
                    ) : (
                      // 2–10 MB band: binary is retained; just a heads-up on duration
                      <> — converting may take {
                        pendingUpload.size >= XL_THRESHOLD ? '~1 minute. ' : '~30 seconds. '
                      }Stay on this page.</>
                    )}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={confirmPendingUpload}
                    className="px-3 py-1 text-[10px] font-mono border border-[#F2A900]/60 text-[#F2A900] hover:bg-[#F2A900]/10"
                    data-testid="upload-confirm-btn"
                  >
                    Upload
                  </button>
                  <button
                    onClick={cancelPendingUpload}
                    className="p-1 text-[#A0A0A0] hover:text-[#F5F1E8]"
                    title="Cancel"
                    data-testid="upload-cancel-btn"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* #749 + #753: Phase-aware progress banner.
            wire/server: blue, file size + elapsed timer + heartbeat pulse +
                         throughput (wire) + size-aware copy (xl) +
                         tab-close warning (xl) + stale-heartbeat warning
            error:       red, with retry/dismiss
            Idle: nothing rendered. */}
        <AnimatePresence>
          {uploadState && (
            <motion.div
              key={uploadState.phase === 'error' ? 'error' : 'progress'}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-4"
              data-testid={`upload-banner-${uploadState.phase}`}
            >
              {(uploadState.phase === 'wire' || uploadState.phase === 'server') && (() => {
                const dispPct = displayPercent(uploadState);
                const elapsed = Date.now() - (uploadState.startedAt || Date.now());
                const isXL = (uploadState.fileSize || 0) >= XL_THRESHOLD;
                const pulse = shouldPulseBar(uploadState);
                // #763: wire phase has two sub-states — still sending
                // vs fully sent + waiting for the server's 202 ack.
                const labelText = uploadState.phase === 'wire'
                  ? (uploadState.wireBytesSent
                      ? 'Waiting for server confirmation'
                      : 'Uploading')
                  : labelForServerPhase(uploadState);
                return (
                  <div className="p-3 bg-[#00D4FF]/10 border border-[#00D4FF]/30">
                    <div className="flex items-center gap-3 mb-2">
                      <Loader2 className="w-4 h-4 text-[#00D4FF] animate-spin shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[#F5F1E8] font-mono text-xs truncate">
                          {labelText}: <span className="text-[#00D4FF]">{uploadState.filename}</span>
                          {uploadState.fileSize ? (
                            <span className="text-[#A0A0A0]"> ({formatBytes(uploadState.fileSize)})</span>
                          ) : null}
                        </p>
                      </div>
                      <span
                        className="text-[#A0A0A0] font-mono text-[10px] tabular-nums shrink-0"
                        data-testid="upload-elapsed"
                      >
                        {formatElapsed(elapsed)}
                      </span>
                      <span
                        className="text-[#00D4FF] font-mono text-xs font-bold tabular-nums shrink-0"
                        data-testid="upload-percent"
                      >
                        {dispPct}%
                      </span>
                    </div>
                    {/* Progress bar — pulses when % hasn't moved in 2s */}
                    <div className="h-1.5 bg-[#0F0F0F] overflow-hidden">
                      <motion.div
                        className={`h-full bg-[#00D4FF] ${pulse ? 'animate-pulse' : ''}`}
                        initial={false}
                        animate={{ width: `${dispPct}%` }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                        data-testid="upload-progress-fill"
                      />
                    </div>
                    {/* Tier 1 + Tier 3 sub-info row */}
                    <div className="flex items-center justify-between gap-3 mt-1.5 text-[10px] font-mono text-[#A0A0A0] min-h-[14px]">
                      <span data-testid="upload-throughput">
                        {uploadState.phase === 'wire' && uploadState.throughputBps > 0
                          ? (uploadState.throughputSlow
                              ? '(slow connection)'
                              : `${formatThroughput(uploadState.throughputBps)}`)
                          : ''}
                      </span>
                      {isXL && (
                        <span
                          className="text-[#F2A900]"
                          data-testid="upload-tab-close-warning"
                        >
                          Closing this tab will cancel the upload.
                        </span>
                      )}
                    </div>
                    {/* Tier 3: stale heartbeat warning */}
                    {uploadState.staleHeartbeat && (
                      <div
                        className="flex items-start gap-2 mt-2 p-2 bg-[#F2A900]/10 border border-[#F2A900]/40 text-[10px] font-mono"
                        data-testid="upload-stale-heartbeat"
                      >
                        <AlertCircle className="w-3 h-3 text-[#F2A900] shrink-0 mt-0.5" />
                        <span className="text-[#F2A900]">
                          Job appears stuck — no heartbeat for {uploadState.secondsSinceHeartbeat}s.
                          You can keep waiting or refresh and check the file list.
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
              {uploadState.phase === 'error' && (
                <div className="flex items-start gap-3 p-3 bg-[#EF4444]/10 border border-[#EF4444]/40 text-sm">
                  <AlertCircle className="w-4 h-4 text-[#EF4444] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#F5F1E8] font-mono text-xs">
                      Upload failed for <span className="text-[#EF4444]">{uploadState.filename}</span>
                      {uploadState.fileSize ? (
                        <span className="text-[#A0A0A0]"> ({formatBytes(uploadState.fileSize)})</span>
                      ) : null}
                    </p>
                    <p className="text-[#A0A0A0] text-[10px] font-mono mt-1 break-all">
                      {uploadState.message}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {/* #757: same-file retry can't succeed for hard errors
                        like size-exceeded — hide the button to avoid
                        implying the user can recover without action. */}
                    {!uploadState.noRetry && (
                      <button
                        onClick={retryUpload}
                        className="px-3 py-1 text-[10px] font-mono border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10"
                        data-testid="upload-retry-btn"
                      >
                        Retry
                      </button>
                    )}
                    <button
                      onClick={dismissUploadBanner}
                      className="p-1 text-[#A0A0A0] hover:text-[#F5F1E8]"
                      title="Dismiss"
                      data-testid="upload-dismiss-btn"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Create form */}
        {showCreate && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-6">
            <div className="flex gap-3 mb-3">
              <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)}
                className="flex-1 px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
                placeholder="Title (optional)" data-testid="context-title-input" />
              <div className="flex gap-1">
                {['prompt', 'research', 'document', 'spec'].map(t => (
                  <button key={t} onClick={() => setNewType(t)}
                    className={`px-2 py-1 text-[9px] font-mono border capitalize ${newType === t ? 'text-black' : 'text-[#A0A0A0] border-[#2A2A2A]'}`}
                    style={newType === t ? { backgroundColor: TYPE_COLORS[t], borderColor: TYPE_COLORS[t] } : {}}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <textarea value={newContent} onChange={e => setNewContent(e.target.value)}
              className="w-full h-48 p-3 bg-[#0F0F0F] border border-[#2A2A2A] text-sm font-mono resize-none focus:outline-none focus:border-[#FF4D00] leading-relaxed"
              placeholder="Paste your prompt, research notes, or document content..." data-testid="context-content-input" />
            <div className="flex justify-between mt-3">
              <span className="text-[10px] font-mono text-[#A0A0A0]">{newContent.length} chars</span>
              <div className="flex gap-2">
                <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 text-xs text-[#A0A0A0] border border-[#2A2A2A]">Cancel</button>
                <button onClick={handleSave} disabled={saving || !newContent.trim()}
                  className="px-4 py-1.5 bg-[#FF4D00] text-black text-xs font-medium disabled:opacity-50" data-testid="save-context-btn">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="w-4 h-4 text-[#A0A0A0] absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
            placeholder="Search saved contexts..." data-testid="context-search" />
        </div>

        {/* Context list */}
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 animate-shimmer" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-[#2A2A2A]" data-testid="context-empty">
            <FileText className="w-10 h-10 text-[#2A2A2A] mx-auto mb-4" />
            <p className="text-[#A0A0A0] mb-4">No saved contexts yet.</p>
            <p className="text-xs text-[#A0A0A0]">Save prompts and research documents here to reuse them across Frays.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="context-list">
            {filtered.map((ctx, i) => (
              <motion.div key={ctx.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <div className="bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF4D00]/20 transition-colors" data-testid={`context-item-${i}`}>
                  <div className="p-4 flex items-start justify-between cursor-pointer" onClick={() => handleExpand(ctx.id)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[9px] px-1.5 py-0.5 border font-mono capitalize"
                          style={{ color: TYPE_COLORS[ctx.type] || '#A0A0A0', borderColor: (TYPE_COLORS[ctx.type] || '#A0A0A0') + '40' }}>
                          {ctx.type}
                        </span>
                        <span className="text-sm font-medium font-['Barlow_Condensed'] truncate">{ctx.title}</span>
                        <BinaryStatusBadge status={ctx.binary_status} />
                      </div>
                      <p className="text-xs text-[#A0A0A0] line-clamp-2">{ctx.preview}</p>
                      <span className="text-[9px] font-mono text-[#A0A0A0] mt-1 block">
                        {ctx.char_count?.toLocaleString()} chars | {new Date(ctx.created_at).toLocaleDateString()}
                        {ctx.filename && ` | ${ctx.filename}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-3">
                      {/* #767: markdown download — always available when
                          the row has a markdown rendering. Lets the user
                          verify what Frays will actually read. */}
                      {(ctx.conversion_status === 'completed'
                        || ctx.conversion_status === 'fallback_text'
                        || ctx.conversion_status === 'not_needed'
                        || ctx.binary_status === 'not_applicable') && (
                        <button onClick={(e) => { e.stopPropagation(); handleDownload(ctx, 'markdown'); }}
                          className="p-1.5 text-[#A0A0A0] hover:text-[#00D4FF]"
                          title="Download markdown (what Fray reads)"
                          data-testid="download-markdown-btn">
                          <FileText className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {/* #767: original download — only when binary is
                          actually retained. Hidden for not_retained /
                          failed / not_found rows. */}
                      {ctx.binary_status === 'stored' && (
                        <button onClick={(e) => { e.stopPropagation(); handleDownload(ctx, 'original'); }}
                          className="p-1.5 text-[#A0A0A0] hover:text-[#00D4FF]"
                          title="Download original file"
                          data-testid="download-original-btn">
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); handleExpand(ctx.id).then(() => handleCopyToImport(expandedContent)); }}
                        className="p-1.5 text-[#A0A0A0] hover:text-[#FF4D00]" title="Use in Import Prompt">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(ctx.id); }}
                        className="p-1.5 text-[#A0A0A0] hover:text-[#EF4444]" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {/* Expanded content */}
                  {expandedId === ctx.id && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} className="border-t border-[#2A2A2A]">
                      <pre className="p-4 text-xs text-[#F5F5F5] font-mono whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">{expandedContent}</pre>
                      <div className="p-3 border-t border-[#2A2A2A] flex gap-2">
                        <button onClick={async () => {
                          const res = await API.get(`/api/contexts/${ctx.id}`);
                          navigator.clipboard.writeText(res.data.content);
                          setCopied(ctx.id);
                          setTimeout(() => setCopied(''), 2000);
                        }} className="px-3 py-1.5 text-xs border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F5F5F5] flex items-center gap-1">
                          {copied === ctx.id ? <><CheckCircle className="w-3 h-3 text-[#22C55E]" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                        </button>
                        <button onClick={async () => {
                          const res = await API.get(`/api/contexts/${ctx.id}`);
                          navigator.clipboard.writeText(res.data.content);
                          navigate('/fray/import');
                        }} className="px-3 py-1.5 text-xs bg-[#FF4D00] text-black font-medium flex items-center gap-1" data-testid={`use-context-${i}`}>
                          <ExternalLink className="w-3 h-3" /> Use in Import
                        </button>
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
